import { createContext, useContext, useMemo, FunctionComponent, ReactNode } from 'react';
import { UserPermissions } from 'services/user-permissions';
import { PERMISSIONS } from 'configuration/config';

interface PermissionsContextValue {
    hasGspTracker: boolean;
    hasSoftwareProcurement: boolean;
}

const PermissionsContext = createContext<PermissionsContextValue>({
    hasGspTracker: false,
    hasSoftwareProcurement: false,
});

/**
 * Provides permission flags to the component tree.
 * Must be rendered AFTER AuthLoading (permissions are already loaded).
 * All consumers re-render when the provider value changes.
 */
export const PermissionsProvider: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
    const hasGspTracker = UserPermissions.instance.hasAuthorized
        && UserPermissions.instance.hasPermission(PERMISSIONS.TOGGLE_GSP_TRACKER);
    const hasSoftwareProcurement = UserPermissions.instance.hasAuthorized
        && UserPermissions.instance.hasPermission(PERMISSIONS.SOFTWARE_PROCUREMENT);

    const value = useMemo(
        () => ({ hasGspTracker, hasSoftwareProcurement }),
        [hasGspTracker, hasSoftwareProcurement],
    );

    return (
        <PermissionsContext.Provider value={value}>
            {children}
        </PermissionsContext.Provider>
    );
};

/**
 * Hook to access permission flags from any component in the tree.
 */
export const usePermissions = (): PermissionsContextValue => {
    return useContext(PermissionsContext);
};
